import React, { useState } from "react";
import { Modal, Form, Input, Checkbox } from "antd";
import PersonSearch from "components/PersonSearch";
import ApproverSearch from "components/ApproverSearch";
import { useSelector } from "react-redux";
import RoleSelector from "features/capex/role/RoleSelector";
import {
	selectAllWorkflows,
	selectWorkflowTypes,
} from "reducers/workflowsSlice";
import TypeSelector from "features/approval/TypeSelector";
import SequenceSelector from "features/approval/SequenceSelector";
import PersonSearchAd from "components/PersonSearchAd";
import { selectGlobal } from "reducers/global";
import { selectUserOrganisation } from "reducers/securitySlice";

const { TextArea } = Input;

const Comment = (props) => {
	/*
  comments are tied to events in a form
  to be independent from event logic, this component needs
  1. a dispatcher to call onSuccess - handle database and redux
     this will handle the other data that Comment doesn't know about
  2. know a list of valid events and what fields/labels it needs
  3. a dispatcher dedicated to comment and event logging
  */
	const [form] = Form.useForm();
	const [state, setState] = useState();
	const [userId, setUserId] = useState(null);
	const [changeUserId, setChangeUserId] = useState(null);
	const [roleId, setRoleId] = useState(null);
	const [sequence, setSequence] = useState(null);
	const [workflowType, setWorkflowType] = useState(null);
	const {
		type,
		onOk,
		onCancel,
		initialValues,
		defaultOptions,
		...otherProps
	} = props;

	const { label, title, required, footer } = COMMENT_LABELS.find(
		(f) => f.type === type
	) || { label: "", title: "", required: false, footer: null };

	const { appId } = useSelector(selectGlobal);
	const types = useSelector(selectWorkflowTypes);
	const workflows = useSelector(selectAllWorkflows);
	const org = useSelector(selectUserOrganisation);

	const setWorkflowTypeAndClearSequence = (value) => {
		setSequence(null);
		setWorkflowType(value);
	};

	return (
		<Modal
			{...otherProps}
			onOk={() => {
				form.validateFields()
					.then((values) => {
						form.resetFields();
						onOk(values);
					})
					.catch((info) => {
						console.log("Validate Failed:", info);
					});
			}}
			onCancel={() => {
				form.resetFields();
				setUserId(null);
				setChangeUserId(null);
				onCancel();
			}}
			maskClosable={false}
			destroyOnClose={true}
		>
			<h1>{title}</h1>
			<Form
				form={form}
				name="commentForm"
				layout="vertical"
				validateTrigger="onChange"
				initialValues={initialValues}
				preserve={false}
			>
				{type === "Change" && (
					<Form.Item
						label="Change To"
						name="changeUserId"
						rules={[
							{
								required: true,
								message: "Change to is mandatory",
							},
						]}
						valuePropName="value"
						trigger="onChange"
					>
						{process.env.REACT_APP_CAPEX_AUTOREGISTER === "true" ? (
							<PersonSearchAd
								value={changeUserId}
								onChange={setChangeUserId}
								options={{ valueKey: "EmailAddress" }}
							/>
						) : (
							<ApproverSearch
								roleId={defaultOptions[0].roleId}
								organisationId={
									defaultOptions[0].organisationId
								}
								domain={org}
								value={changeUserId}
								onChange={setChangeUserId}
							/>
						)}
					</Form.Item>
				)}
				{(type === "Query" ||
					type === "Delegate" ||
					type === "Add") && (
					<Form.Item
						label="Person To"
						name="userId"
						rules={[
							{
								required: true,
								message: "Person to is mandatory",
							},
						]}
						valuePropName="value"
						trigger="onChange"
					>
						{process.env.REACT_APP_CAPEX_AUTOREGISTER === "true" ? (
							<PersonSearchAd
								value={changeUserId}
								onChange={setChangeUserId}
								options={{ valueKey: "EmailAddress" }}
							/>
						) : (
							<PersonSearch
								appHierarchyId={appId}
								defaultOptions={defaultOptions}
								value={userId}
								onChange={setUserId}
							/>
						)}
					</Form.Item>
				)}
				{type === "Add" && (
					<>
						<Form.Item label="Workflow Role" name="roleId">
							<RoleSelector value={roleId} onChange={setRoleId} />
						</Form.Item>
						{types && types.length > 0 && (
							<Form.Item
								label="Type of Workflow"
								name="type"
								rules={[
									{
										required: true,
										message: "Type is mandatory",
									},
								]}
							>
								<TypeSelector
									value={workflowType}
									onChange={setWorkflowTypeAndClearSequence}
									types={types}
								/>
							</Form.Item>
						)}

						{(!types || !types.length > 0 || workflowType) && (
							<Form.Item
								label="Sequence"
								name="sequence"
								rules={[
									{
										required: true,
										message:
											"Please choose where this workflow should go",
									},
								]}
								dependencies={["type"]}
							>
								<SequenceSelector
									value={sequence}
									onChange={setSequence}
									sequences={workflows.filter(
										(w) =>
											w.type === workflowType ||
											!types ||
											!types.length > 0
									)}
								/>
							</Form.Item>
						)}
					</>
				)}
				<Form.Item
					label={label}
					name="comments"
					rules={[
						{
							required: required,
							message: "Comments are mandatory",
						},
						{
							max: 1000,
							message: "Comments can't exceed 1,000 characters",
						},
					]}
				>
					<TextArea
						rows={5}
						value={state}
						onChange={(e) => setState(e.target.value)}
					/>
				</Form.Item>
				{footer}
			</Form>
		</Modal>
	);
};

export const COMMENT_LABELS = [
	{
		type: "Approve",
		label: "Comments (optional)",
		title: "Approve",
		required: false,
	},
	{
		type: "ApproveCme",
		label: "Comments (optional)",
		title: "Approve",
		required: false,
		footer: (
			<div style={{ color: "#777", marginTop: 10 }}>
				I confirm this expense meets the standards outlined in the CME
				policy and is relevant to my education.
			</div>
		),
	},
	{
		type: "Decline",
		label: "Please provide comments for your decision",
		title: "Decline",
		required: true,
	},
	{
		type: "Submit",
		label: "Please provide comments for the approvers",
		title: "Submit",
		required: false,
	},
	{
		type: "SubmitCme",
		label: "Comments (optional)",
		title: "Submit",
		required: false,
		footer: (
			<Form.Item
				name="confirmation"
				label="Declaration"
				rules={[
					(_) => ({
						message: "Confirmation is required",
						validator(_, value) {
							if (value === true) {
								return Promise.resolve();
							} else return Promise.reject(new Error());
						},
					}),
				]}
				validateTrigger="onSubmit"
				valuePropName="checked"
			>
				<Checkbox>
					I confirm this expense meets the standards outlined in the
					District's CME / CPD policy and is relevant to education.
				</Checkbox>
			</Form.Item>
		),
	},
	{
		type: "Resume",
		label: "Please provide comments for the approvers",
		title: "Resubmit",
		required: false,
	},
	{
		type: "Delegate",
		label: "Please provide comments for your delegation reason",
		title: "Delegate",
		required: false,
	},
	{
		type: "Add",
		label: "(Optional) Please provide comments on why this person is being added",
		title: "Add",
		required: false,
	},
	{
		type: "Query",
		label: "Please provide details of your query here",
		title: "Query",
		required: false,
	},
	{
		type: "Change",
		label: "Comments",
		title: "Change approver",
		required: false,
	},
	{
		type: "Lock",
		label: "Comments (optional)",
		title: "Lock Form from Editing",
		required: false,
	},
	{
		type: "Unlock",
		label: "Please provide comments on why you are unlocking the form",
		title: "Unlock Form for Editing",
		required: true,
	},
	{
		type: "Delete",
		label: "Comments",
		title: "Delete approver",
		required: false,
	},
	{
		type: "Respond",
		label: "Please provide comments in response to the query you received",
		title: "Respond to Query",
		required: true,
	},
];

export default Comment;
